import { useEffect, useState } from 'react';
import { useLoginActivity, useTeams } from '../api/hooks';
import type { LoginSession, SessionState } from '../api/types';
import { StatTile } from '../components/charts';
import { EmptyState, ErrorBanner, TableSkeleton, formatDateTime, relativeTime } from '../components/ui';

const PAGE_SIZE = 50;

const ROLE_FILTERS: Array<{ value: string; label: string; roles: string[] }> = [
  { value: 'staff', label: 'Managers, team leads & specialists', roles: ['manager', 'team_lead', 'counsellor'] },
  { value: 'manager', label: 'Managers', roles: ['manager'] },
  { value: 'team_lead', label: 'Team Leads', roles: ['team_lead'] },
  { value: 'counsellor', label: 'Team Specialists', roles: ['counsellor'] },
  { value: 'admin', label: 'Admins', roles: ['admin', 'super_admin'] },
  { value: 'all', label: 'Everyone', roles: [] },
];

const STATE_LABEL: Record<SessionState, string> = {
  active: 'Active now',
  idle: 'Signed in · idle',
  logged_out: 'Logged out',
  ended: 'Ended',
  expired: 'Expired',
};

const STATE_COLOR: Record<SessionState, string> = {
  active: 'var(--good)',
  idle: 'var(--warn)',
  logged_out: 'var(--muted)',
  ended: 'var(--danger)',
  expired: 'var(--faint)',
};

const END_REASON: Record<string, string> = {
  password_changed: 'password changed',
  deactivated: 'account deactivated',
};

function duration(seconds: number): string {
  if (seconds < 60) return '< 1 min';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m} min`;
}

/** Browser and OS, roughly — enough to tell "phone" from "office PC". */
function device(ua: string | null): string {
  if (!ua) return '—';
  const os = /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Windows/.test(ua) ? 'Windows'
    : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'Other';
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  return `${browser} · ${os}`;
}

/** yyyy-mm-dd from a date input → ISO instant at local midnight. */
const dayStart = (d: string) => new Date(`${d}T00:00:00`).toISOString();
const dayAfter = (d: string) => {
  const x = new Date(`${d}T00:00:00`);
  x.setDate(x.getDate() + 1);
  return x.toISOString();
};

/**
 * Login activity — when each manager and counsellor signed in and out.
 * Admin only (audit.view).
 */
export function LoginActivityPage() {
  const { data: teams = [] } = useTeams();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('staff');
  const [teamId, setTeamId] = useState('');
  const [state, setState] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [user, setUser] = useState<{ id: string; name: string } | null>(null);
  const [offset, setOffset] = useState(0);

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setOffset(0); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const reset = () => setOffset(0);
  const roles = ROLE_FILTERS.find((r) => r.value === roleFilter)?.roles ?? [];

  const { data, isPending, error, isFetching } = useLoginActivity({
    search: search || undefined,
    role: roles,
    team_id: teamId || undefined,
    user_id: user?.id,
    state: state ? [state] : undefined,
    from: from ? dayStart(from) : undefined,
    to: to ? dayAfter(to) : undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const anyFilter = Boolean(search || roleFilter !== 'staff' || teamId || state || from || to || user);

  function clearAll() {
    setSearchInput(''); setSearch(''); setRoleFilter('staff'); setTeamId(''); setState('');
    setFrom(''); setTo(''); setUser(null); reset();
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Login activity</h1>
          <p className="sub">When each person signed in and out of the CRM · times in your local time zone</p>
        </div>
      </div>

      <div className="kpi-row" style={{ marginBottom: 18 }}>
        <StatTile label="Sessions matching" value={total} hint={isFetching ? 'Updating…' : undefined} />
        <StatTile label="Active now" value={data?.active_now ?? 0} hint="Used the CRM in the last 15 min" />
        <StatTile label="Logins today" value={data?.logins_today ?? 0} hint="Within the current filters" />
      </div>

      <div className="filters">
        <div className="f grow">
          <label htmlFor="la-search">Search</label>
          <input
            id="la-search"
            type="search"
            placeholder="Name or email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="f">
          <label htmlFor="la-role">Role</label>
          <select id="la-role" value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); reset(); }}>
            {ROLE_FILTERS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        {teams.length > 0 && (
          <div className="f">
            <label htmlFor="la-team">Team</label>
            <select id="la-team" value={teamId} onChange={(e) => { setTeamId(e.target.value); reset(); }}>
              <option value="">All teams</option>
              {teams.filter((t) => t.is_active).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
        <div className="f">
          <label htmlFor="la-state">Status</label>
          <select id="la-state" value={state} onChange={(e) => { setState(e.target.value); reset(); }}>
            <option value="">Any status</option>
            {(Object.keys(STATE_LABEL) as SessionState[]).map((s) => (
              <option key={s} value={s}>{STATE_LABEL[s]}</option>
            ))}
          </select>
        </div>
        <div className="f">
          <label htmlFor="la-from">Logged in between</label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input id="la-from" aria-label="From date" type="date" value={from} max={to || undefined} onChange={(e) => { setFrom(e.target.value); reset(); }} />
            <span style={{ color: 'var(--muted)' }}>–</span>
            <input id="la-to" aria-label="To date" type="date" value={to} min={from || undefined} onChange={(e) => { setTo(e.target.value); reset(); }} />
          </div>
        </div>
      </div>

      {(user || anyFilter) && (
        <div className="active-filters">
          {user && (
            <button className="chip chip-plain" onClick={() => { setUser(null); reset(); }} title="Remove filter">
              Person: {user.name} ✕
            </button>
          )}
          {anyFilter && <button className="btn btn-ghost btn-sm" onClick={clearAll}>Clear all</button>}
        </div>
      )}

      <ErrorBanner error={error} />

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Person</th>
              <th>Role</th>
              <th>Logged in</th>
              <th>Logged out</th>
              <th className="num">Duration</th>
              <th>Status</th>
              <th>Device</th>
            </tr>
          </thead>
          {isPending ? (
            <TableSkeleton rows={8} cols={7} />
          ) : (
            <tbody>
              {rows.map((s) => <SessionRow key={s.id} s={s} onPickUser={(u) => { setUser(u); reset(); }} />)}
            </tbody>
          )}
        </table>
      </div>

      {!isPending && rows.length === 0 && (
        <EmptyState title="No sessions found">
          {anyFilter ? 'Try widening the filters.' : 'Sessions appear here as people sign in.'}
        </EmptyState>
      )}

      {total > 0 && (
        <div className="pager">
          <span className="count">
            {(offset + 1).toLocaleString()}–{Math.min(offset + PAGE_SIZE, total).toLocaleString()} of {total.toLocaleString()}
          </span>
          <button className="btn btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            ← Previous
          </button>
          <button className="btn btn-sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function SessionRow({ s, onPickUser }: { s: LoginSession; onPickUser: (u: { id: string; name: string }) => void }) {
  const stillIn = s.state === 'active' || s.state === 'idle';
  return (
    <tr>
      <td>
        <button
          className="link-button"
          style={{ fontSize: 13.5 }}
          title="Show only this person's sessions"
          onClick={() => onPickUser({ id: s.user.id, name: s.user.full_name })}
        >
          {s.user.full_name}
        </button>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          {s.user.email}{s.teams ? ` · ${s.teams}` : ''}
        </div>
      </td>
      <td>{s.roles?.map((r) => r.name).join(', ') ?? '—'}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(s.login_at)}
        {s.login_method === 'carried_over' && (
          <div style={{ fontSize: 11.5, color: 'var(--faint)' }} title="Already signed in when tracking started; actual sign-in was earlier">
            tracking started
          </div>
        )}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {s.logout_at ? formatDateTime(s.logout_at) : (
          <span style={{ color: 'var(--muted)' }}>
            {stillIn ? 'Still signed in' : 'Never signed out'}
            <div style={{ fontSize: 11.5, color: 'var(--faint)' }}>last active {relativeTime(s.last_seen_at)}</div>
          </span>
        )}
      </td>
      <td className="num">{duration(s.duration_seconds)}</td>
      <td>
        <span className="chip chip-plain" style={{ color: STATE_COLOR[s.state] }}>
          {STATE_LABEL[s.state]}
        </span>
        {s.state === 'ended' && s.end_reason && END_REASON[s.end_reason] && (
          <div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 2 }}>{END_REASON[s.end_reason]}</div>
        )}
      </td>
      <td style={{ fontSize: 12.5 }}>
        {device(s.user_agent)}
        {s.ip_address && <div style={{ fontSize: 11.5, color: 'var(--faint)', fontFamily: 'var(--mono)' }}>{s.ip_address}</div>}
      </td>
    </tr>
  );
}
